import React, { useEffect, useState, useRef, useContext, useMemo } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import AddGame from '../../pages/Archive/AddGame';
import ViewGame from '../../pages/ViewGame';
import { BOARD_THEMES, DEFAULT_MOVE_HIGHLIGHT } from '../WorkingChessLogic/BoardStyle';
import { ChessSocialContext } from '../../util/ChessSocialContext';
import { useChessAnalysis } from '../../util/ChessAnalysisContext';
import Moves from './Moves';

// Import Stockfish icons and emoji styles from the new file
import { STOCKFISH_ICON_STYLES, EMOJI_STYLES, stockfishIcons, emojiIcons } from '../../util/ChessIcons';
// Import GG icons for checkmate, check and draw states
import winnerIcon from '../../images/GGIcons/Winner.png';
import loserIcon from '../../images/GGIcons/Loser.png';
import checkIcon from '../../images/GGIcons/Check.png';
import drawIcon from '../../images/GGIcons/Draw.png';

import {
  getCurrentBoardPosition,
  getMove,
  getLines,
  destructureLine,
  parseMove,
  isMoveValid,
  createMove,
  resetPositionCache,
  getPrevMoveNotation
} from '../../util/Chess';
import constants from '../../util/constants';
import zIndex from '@material-ui/core/styles/zIndex';
const COLOR_VARIANTS = constants.CHESSBOARD.COLOR_VARIANTS;
const MOVE_TYPES = constants.CHESSBOARD.MOVE_TYPES;

// Define the move highlight color with 25% opacity
const MOVE_HIGHLIGHT_COLOR = { backgroundColor: 'rgba(255, 215, 0, 0.75)' };

/**
 * Chess Board Component
 * 
 * @param {Object} props Component props
 * @param {Object} props.game Chess.js game object
 * @param {string} props.orientation Board orientation ('white' or 'black')
 * @param {Array} props.moves Array of moves
 * @param {Function} props.setMoves Function to update moves
 * @param {string} props.currentMove Current move index
 * @param {Function} props.setCurrentMove Function to update current move
 * @param {boolean} props.isCommenting Whether we're in commenting mode
 * @param {Function} props.setPgn Function to update PGN
 * @param {number} props.boardSize Size of the chess board
 * @param {string} props.boardTheme Theme of the board ('blue', 'green', etc.)
 * @param {boolean} props.showBoardNotation Whether to show board notation
 * @param {boolean} props.isDraggingDisabled Whether dragging pieces is disabled
 * @param {string} props.emojiStyle Style of emoji to show on moves
 *                                 Options: 'target', 'exclamation', 'smile', 'sad', 'wow',
 *                                 'angry', 'love', 'thinking', 'laugh', 'surprised', 'confused'
 * @param {string} props.stockfishIconStyle Style of stockfish icon to show on moves
 *                                        Options: 'best', 'brilliant', 'blunder', 'book', 'great',
 *                                        'good', 'interesting', 'mistake', 'inaccuracy', 'miss'
 * @param {Function} props.onValidateMove Function to validate moves (for puzzles)
 * @param {Function} props.shouldAllowVariation Function to check if variations should be allowed
 * @param {boolean} props.isPuzzle Whether this is a puzzle board
 * @param {Object} props.gameResultInfo Game result info
 * @param {Function} props.onFenChange Callback for when the FEN position changes
 */
const Board = ({
  game,
  orientation,
  moves,
  setMoves,
  currentMove,
  setCurrentMove,
  isCommenting = false,
  setPgn,
  boardSize,
  boardTheme = 'blue',
  showBoardNotation,
  isDraggingDisabled = false,
  emojiStyle = 'target', // Default emoji style
  stockfishIconStyle = 'best', // Default stockfish icon style
  onValidateMove = null, // Function to validate moves (for puzzles)
  shouldAllowVariation = () => true, // Function to check if variations should be allowed
  isPuzzle = false, // Whether this is a puzzle board
  gameResultInfo = null, // Add this line to accept game result info
  type,
  userId,
  className,
  handleVariationNavigation,
  commentPreviewMoves,
  setCommentPreviewMoves,
  isAnnotationPreview,
  onFenChange = null, // Add callback for FEN changes
}) => {
  // Access the global context
  const {
    showEmoji,
    showStockfishIcons,
    showArrows,
    stockfishArrows
  } = useContext(ChessSocialContext);


  // Use the chess analysis context
  const { updatePositionSilently, checkForExistingAnalysis } = useChessAnalysis();

  const [activeColor, setActiveColor] = useState(COLOR_VARIANTS.RED);
  const [moveSquares, setMoveSquares] = useState({});
  const [lastMovedSquare, setLastMovedSquare] = useState(null); // Track the last square a piece moved to
  const [currentEmojiStyle, setCurrentEmojiStyle] = useState(emojiStyle); // Track the current emoji style
  const [emojiSquares, setEmojiSquares] = useState({}); // Track squares with emojis separately
  const [squareSize, setSquareSize] = useState(0); // Add state for square size calculation

  // New state variables for Stockfish icons
  const [currentStockfishStyle, setCurrentStockfishStyle] = useState(stockfishIconStyle); // Track current icon style
  const [stockfishSquares, setStockfishSquares] = useState({}); // Track squares with stockfish icons

  // Add new state for tracking kings and game end state
  const [kingPositions, setKingPositions] = useState({ white: null, black: null });
  const [gameState, setGameState] = useState({
    isCheckmate: false,
    isCheck: false,
    isDraw: false,
    winner: null
  });

  // Add a new state to track the latest classified position
  const [classifiedPosition, setClassifiedPosition] = useState(null);

  // Get the current theme colors
  const currentTheme = BOARD_THEMES[boardTheme] || BOARD_THEMES.blue;

  // Get custom colors from localStorage if the theme is 'custom'
  const [customColors] = useState(() => {
    if (boardTheme === 'custom') {
      const savedCustomColors = localStorage.getItem('customColors');
      return savedCustomColors ? JSON.parse(savedCustomColors) : {
        dark: '#3066be',
        light: '#eeeed2'
      };
    }
    return null;
  });

  // Add state for tracking starting turn
  const [startingTurn, setStartingTurn] = useState('w');

  // Create a ref to track the last processed move
  const lastProcessedMoveRef = useRef(null);

  // Add reference to track the last rendered position
  const lastRenderedPositionRef = useRef(null);

  // Memoize the target position to avoid redundant calculations
  const targetPosition = useMemo(() => {
    // Check for direct variation move flag
    const isDirectVariationMove = window.chessDirectVariationMove && 
      window.chessDirectVariationMove.moveNotation === currentMove &&
      (Date.now() - window.chessDirectVariationMove.timestamp) < 1000; // Valid for 1 second
    
    // Always clear the position cache for any move change to ensure fresh calculation
    resetPositionCache();
    
    // Get position from game
    const position = getCurrentBoardPosition(game, currentMove);
    
    console.log('[BOARD-FEN] useMemo targetPosition:', {
      currentMove,
      position,
      previousPosition: lastRenderedPositionRef.current,
      isDirectVariationMove,
      isVariationMove: currentMove.includes('|')
    });
    
    // If this is a direct variation move, immediately update the lastRenderedPosition reference
    // This prevents animation from previous position
    if (isDirectVariationMove && position) {
      
      lastRenderedPositionRef.current = position;
      
      // Clear the flag to prevent reuse
      try {
        window.chessDirectVariationMove = null;
      } catch (e) {
        console.error('[BOARD-FEN] Error clearing window flag', e);
      }
    }
    
    // Pre-emptively calculate move squares once we have the position
    // This ensures move highlighting updates synchronously with the position
    if (position && position !== lastRenderedPositionRef.current) {
      // Use setTimeout with 0ms to ensure this runs after the render
      setTimeout(() => {
        updateMoveSquaresForPosition(currentMove);
      }, 0);
    }
    
    return position;
  }, [game, currentMove]);

  // Effect to determine starting turn from FEN
  useEffect(() => {
    if (game) {
      try {
        // Get the initial position's FEN
        const initialFen = getCurrentBoardPosition(game, '-1');
        
        if (initialFen && typeof initialFen === 'string') {
          // FEN format: position turn castling enpassant halfmove fullmove
          // Extract the turn from the second field
          const fenParts = initialFen.split(' ');
          if (fenParts && fenParts.length >= 2) {
            setStartingTurn(fenParts[1]);
            
          } else {
            console.warn('Invalid FEN format for starting turn detection:', initialFen);
            setStartingTurn('w'); // Default to white if FEN is invalid
          }
        } else {
          console.warn('No valid initial FEN available, defaulting to white');
          setStartingTurn('w'); // Default to white if no FEN is available
        }
      } catch (error) {
        console.error('Error parsing initial FEN:', error);
        setStartingTurn('w'); // Default to white on error
      }
    }
  }, [game]);

  // Initial setup effect - runs once on mount to initialize currentPosition
  useEffect(() => {
    if (game) {
      const initialFen = getCurrentBoardPosition(game, currentMove);
      if (initialFen) {
        // Check for N+1 move's analysis data on initial mount
        const currentMoveIdx = parseInt(currentMove);
        const nextMoveIdx = currentMoveIdx + 1;

        if (moves && nextMoveIdx < moves.length) {
          // Get the next move's data
          const nextMoveData = getMove(moves, nextMoveIdx.toString());
          const hasNextMoveAnalysis = !!(nextMoveData?.analysis?.pvs || nextMoveData?.stockfish);

          console.log('Board.jsx: Initial mount - checking N+1 move for analysis', {
            currentMove,
            nextMoveIdx,
            hasNextMoveAnalysis
          });

          // If the next move has analysis data, populate the global window objects
          if (hasNextMoveAnalysis) {
            const analysisData = nextMoveData.analysis || nextMoveData.stockfish;

            // Update window cache with N+1 move's analysis data
            window.currentAnalysisCache = analysisData;

            // Update classification data if available
            if (analysisData.classification) {
              window.moveClassificationData = {
                classification: analysisData.classification,
                positionFEN: initialFen,
                timestamp: Date.now()
              };
            }

            
          }
        }

        // Notify parent about initial FEN
        if (onFenChange) {
          onFenChange(initialFen);
        }
      }
    }
  }, []);

  // Update the current position in the context whenever the board position changes
  useEffect(() => {
    if (game) {
      // No need to recalculate the position here, use our memoized value
      const currentFen = targetPosition;
      if (currentFen && currentFen !== lastRenderedPositionRef.current) {
        // Only update if the position has actually changed
        updatePositionSilently(currentFen);
        lastRenderedPositionRef.current = currentFen;
      }
    }
  }, [targetPosition, game, updatePositionSilently]);

  // Update emoji style
  useEffect(() => {
    setCurrentEmojiStyle(emojiStyle);
  }, [emojiStyle]);

  // Update stockfish icons style
  useEffect(() => {
    setCurrentStockfishStyle(stockfishIconStyle);
  }, [stockfishIconStyle]);

  // Calculate square size when boardSize changes
  useEffect(() => {
    // Square size is the board size divided by 8 (8x8 chess board)
    setSquareSize(boardSize / 8);
  }, [boardSize]);

  // Get coordinates preference from localStorage if not provided as prop
  const shouldShowCoordinates = showBoardNotation ??
    (localStorage.getItem('showCoordinates') === null ? true : localStorage.getItem('showCoordinates') === 'true');

  const handleKeyDown = (event) => {
    let newColor = COLOR_VARIANTS.RED;
    switch (event.key) {
      case "1":
        newColor = COLOR_VARIANTS.BLUE;
        break;
      case "2":
        newColor = COLOR_VARIANTS.GREEN;
        break;
      case "3":
        newColor = COLOR_VARIANTS.YELLOW;
        break;
      default:
        newColor = COLOR_VARIANTS.RED;
    }
    setActiveColor(newColor);
  };

  const handleKeyUp = () => {
    setActiveColor(COLOR_VARIANTS.RED); // Revert to default color when no key is pressed
  };

  useEffect(() => {
    document.removeEventListener("keypress", handleKeyDown);
    document.removeEventListener("keyup", handleKeyUp);

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Function to update move squares based on current position
  const updateMoveSquaresForPosition = (moveIndex) => {
    if (!moveIndex || moveIndex === '-1') {
      setMoveSquares({});
      setLastMovedSquare(null); // Also clear lastMovedSquare
      return;
    }

    // Skip if this is a duplicate update for the same position
    if (lastProcessedMoveRef.current === moveIndex) {
      return;
    }

    // Get the move that led to this position
    const move = getMove(moves, moveIndex);
    if (!move || !move.notation) {
      setMoveSquares({});
      setLastMovedSquare(null); // Also clear lastMovedSquare
      return;
    }

    try {
      // For variation moves, we need special handling to ensure correct highlighting
      if (moveIndex.includes('|')) {
        // Force immediate calculation by getting fresh position from game
        const currentFen = getCurrentBoardPosition(game, moveIndex);
        const parts = moveIndex.split('|');
        const parentMove = parts[0];
        const prevPosition = getCurrentBoardPosition(game, parentMove);
        
        // Create a temporary game to calculate the move squares for this variation
        const tempGame = new Chess(prevPosition);
        
        // Find the move that would produce this position
        try {
          // Look for the matching move in the moves array
          const moveSan = move.notation;
          const moveDetails = tempGame.moves({ verbose: true })
            .find(m => m.san === moveSan);
            
          if (moveDetails) {
            // Create an object with the start and end squares highlighted
            const highlightSquares = {
              [moveDetails.from]: MOVE_HIGHLIGHT_COLOR,
              [moveDetails.to]: MOVE_HIGHLIGHT_COLOR
            };
            setMoveSquares(highlightSquares);
            setLastMovedSquare(moveDetails.to);
            return;
          }
        } catch (varError) {
          console.error("Error highlighting variation move:", varError);
        }
      }
      
      // Original logic for main line moves (unchanged)
      // Create a temporary chess instance to get the move details
      const prevPosition = getCurrentBoardPosition(game, getPrevMoveNotation(moveIndex));

      // To find the destination square, we'll create a temporary game at the previous position
      // and make the move to see where it lands
      const tempGame = new Chess(prevPosition);
      const moveDetails = tempGame.moves({ verbose: true })
        .find(m => m.san === move.notation);

      if (moveDetails) {
        // Create an object with the start and end squares highlighted
        const highlightSquares = {
          [moveDetails.from]: MOVE_HIGHLIGHT_COLOR,
          [moveDetails.to]: MOVE_HIGHLIGHT_COLOR
        };
        setMoveSquares(highlightSquares);

        // Set the last moved square for emoji/stockfish display
        setLastMovedSquare(moveDetails.to);
      } else {
        // If we can't find move details the standard way, try an alternative approach
        // This is a fallback to ensure we always have a lastMovedSquare
        const currentFen = getCurrentBoardPosition(game, moveIndex);
        const tempGame2 = new Chess(currentFen);
        const history = tempGame2.history({ verbose: true });
        if (history.length > 0) {
          // Get the last move from current position's history
          const lastMove = history[history.length - 1];
          setMoveSquares({
            [lastMove.from]: MOVE_HIGHLIGHT_COLOR,
            [lastMove.to]: MOVE_HIGHLIGHT_COLOR
          });
          setLastMovedSquare(lastMove.to);
        } else {
          setMoveSquares({});
          setLastMovedSquare(null);
        }
      }
    } catch (error) {
      console.error("Error updating move squares:", error);
      setMoveSquares({});
      setLastMovedSquare(null);
    }
  };

  // Update to monitor currentMove changes
  useEffect(() => {
    // Skip processing if this move was already processed
    if (lastProcessedMoveRef.current === currentMove) {
      
      return;
    }

    console.log('[BOARD-FEN] Processing new move:', {
      currentMove,
      lastProcessed: lastProcessedMoveRef.current,
      isVariation: currentMove.includes('|')
    });

    // For variation moves accessed via controls, set a direct variation flag ourselves
    if (currentMove.includes('|') && !window.chessDirectVariationMove) {
      window.chessDirectVariationMove = {
        moveNotation: currentMove,
        timestamp: Date.now()
      };
    }

    // Update the ref to the current move
    lastProcessedMoveRef.current = currentMove;

    // Update move squares immediately when current move changes
    // This ensures highlighting is in sync with position changes
    updateMoveSquaresForPosition(currentMove);

    // Get the current move data and FEN - using our memoized position
    const moveData = getMove(moves, currentMove);
    const currentFen = targetPosition;

    

    // Check if this move already has stockfish analysis and populate global data
    const hasStockfishData = !!(moveData?.analysis?.pvs || moveData?.stockfish);

    // Log moves array to check stockfish data
    console.log('Board.jsx: Move change detected', {
      currentMove,
      moveData: moveData,
      hasStockfishData: hasStockfishData,
      currentFen
    });

    // Check for N+1 move's analysis data, which represents analysis for the current position
    const currentMoveIdx = parseInt(currentMove);
    const nextMoveIdx = currentMoveIdx + 1;

    if (nextMoveIdx < moves.length) {
      // Get the next move's data
      const nextMoveData = getMove(moves, nextMoveIdx.toString());
      const hasNextMoveAnalysis = !!(nextMoveData?.analysis?.pvs || nextMoveData?.stockfish);

      console.log('Board.jsx: Checking N+1 move for analysis', {
        nextMoveIdx,
        hasNextMoveAnalysis,
        nextMoveData
      });

      // If the next move has analysis data, check if it matches the current position
      if (hasNextMoveAnalysis) {
        const analysisData = nextMoveData.analysis || nextMoveData.stockfish;

        // Get FEN from move N+1's analysis if available, otherwise use current FEN
        const analysisFen = analysisData.positionFEN || currentFen;

        // Only use the analysis if it's for the current position
        // Compare FENs but ignore the move counters at the end (last 2 fields)
        if (!currentFen || !analysisFen) {
          console.log('Board.jsx: Cannot compare FENs, one or both FENs are undefined', {
            currentFen,
            analysisFen
          });
          return; // Exit early if either FEN is undefined
        }

        // Add safeguards for split operations
        let currentFenBase = '';
        let analysisFenBase = '';

        try {
          if (typeof currentFen === 'string') {
            currentFenBase = currentFen.split(' ').slice(0, 4).join(' ');
          }
          
          if (typeof analysisFen === 'string') {
            analysisFenBase = analysisFen.split(' ').slice(0, 4).join(' ');
          }
          
          console.log('Board.jsx: Comparing FENs for position match', {
            currentFenBase,
            analysisFenBase,
            isMatch: currentFenBase === analysisFenBase
          });
        } catch (error) {
          console.error('Board.jsx: Error comparing FENs:', error, {
            currentFen,
            analysisFen
          });
          return; // Exit early if there's an error
        }

        // If the FENs match (same position), use this analysis
        if (currentFenBase && analysisFenBase && currentFenBase === analysisFenBase) {
          // Update window cache with N+1 move's analysis data
          window.currentAnalysisCache = analysisData;

          // Update classification data if available
          if (analysisData.classification) {
            window.moveClassificationData = {
              classification: analysisData.classification,
              positionFEN: currentFen,
              timestamp: Date.now()
            };
          }

          
        } else {
          
        }
      }
    }

    // If we have stockfish data in the current move, populate the global window data to prevent recalculation
    if (hasStockfishData && currentFen) {
      

      // IMPORTANT: First check for existing analysis and populate window objects
      // before updating the position silently
      const dataPopulated = checkForExistingAnalysis(moveData, currentFen);

      // Now that global data is populated, update the position silently
      // This ensures window.moveClassificationData is available for Stockfish.js
      if (dataPopulated) {
        
        updatePositionSilently(currentFen);
      } else {
        // If no data was populated, just update position without expecting cached analysis
        updatePositionSilently(currentFen);
      }
    } else {
      // If no analysis data was found, update the position silently
      updatePositionSilently(currentFen);
    }

    // Clear stockfish icons immediately when a move is made
    setStockfishSquares({});

    // Also update king positions and game state
    if (currentFen) {
      const tempChess = new Chess(currentFen);
      updateGameState(tempChess);

      // Check if this is the last move and we need to apply game result
      const moveArray = Array.isArray(moves) ? moves : [];
      const isLastMove = moveArray.length > 0 && currentMove === (moveArray.length - 1).toString();

      if (isLastMove && gameResultInfo) {
        // For draw result, immediately update state (don't wait for the next effect)
        if (gameResultInfo.result === '1/2-1/2') {
          setGameState(prevState => ({ ...prevState, isDraw: true }));
        }
      }

      // Notify parent component about FEN change
      if (onFenChange) {
        onFenChange(currentFen);
      }
    }
  }, [currentMove, moves, game, gameResultInfo, onFenChange]);

  // Update emoji squares when emoji display is toggled or current move changes
  useEffect(() => {
    // Clear any existing emoji squares first
    setEmojiSquares({});

    // If emoji display is enabled and we have a last moved square for the current position
    if (showEmoji && lastMovedSquare) {
      // Set emoji only on the current move's target square
      setEmojiSquares({ [lastMovedSquare]: currentEmojiStyle });
    }
  }, [showEmoji, currentEmojiStyle, lastMovedSquare, currentMove]);

  // Add a new effect to check for classification updates
  useEffect(() => {
    // Check for classification data
    if (window.moveClassificationData?.classification &&
      window.moveClassificationData?.positionFEN) {

      // Get the current position from the board
      const currentFen = getCurrentBoardPosition(game, currentMove);

      // Check if this classification is for the current position
      if (currentFen && currentFen === window.moveClassificationData.positionFEN) {
        // This classification is for the current position - display it
        setClassifiedPosition(currentFen);
      }
    }

    // Run this effect more frequently to catch classification updates
    const checkInterval = setInterval(() => {
      // Trigger re-render to check for new classification data
      setClassifiedPosition(prev => {
        if (prev !== window.moveClassificationData?.positionFEN) {
          return window.moveClassificationData?.positionFEN;
        }
        return prev;
      });
    }, 500); // Check every 500ms

    return () => clearInterval(checkInterval);
  }, [game, currentMove]);

  // Update stockfish icons when display is toggled or current move changes
  useEffect(() => {
    // Clear any existing stockfish squares first
    setStockfishSquares({});

    // Get the current position
    const currentFen = getCurrentBoardPosition(game, currentMove);

    // Only show icon if we have a classification for the current position
    if (showStockfishIcons && lastMovedSquare &&
      window.moveClassificationData?.classification &&
      window.moveClassificationData?.positionFEN === currentFen) {

      // Get the classification
      const classification = window.moveClassificationData.classification;

      // Set the appropriate icon based on the classification
      // Make sure we map our classification values to valid icon keys
      let iconStyle = classification;

      // Ensure the icon style exists in our available styles, fallback to default if not
      if (!stockfishIcons[iconStyle]) {
        iconStyle = currentStockfishStyle; // Fallback to the default
      }

      setStockfishSquares({ [lastMovedSquare]: iconStyle });
    } else if (showStockfishIcons && lastMovedSquare) {
    }
  }, [showStockfishIcons, currentStockfishStyle, lastMovedSquare, currentMove, classifiedPosition]);

  // Function to find the positions of both kings on the board
  const findKingPositions = (fen) => {
    const chess = new Chess(fen);
    const board = chess.board();
    const positions = { white: null, black: null };

    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && piece.type === 'k') {
          const square = String.fromCharCode(97 + file) + (8 - rank);
          positions[piece.color === 'w' ? 'white' : 'black'] = square;
        }
      }
    }

    return positions;
  };

  // Update to check game state after a move
  const updateGameState = (chess) => {
    const isCheckmate = chess.isCheckmate();
    const isCheck = chess.isCheck();
    // Base draw detection on chess.js functions
    let isDraw = chess.isDraw() || chess.isStalemate() || chess.isThreefoldRepetition() || chess.isInsufficientMaterial();

    // Also consider the game result info for draw detection
    // This is crucial for PGN that ends with "1/2-1/2"
    if (gameResultInfo && gameResultInfo.result === '1/2-1/2') {
      isDraw = true;
    }

    // Determine winner in case of checkmate
    let winner = null;
    if (isCheckmate) {
      // If it's checkmate, the winner is opposite of the current turn
      winner = chess.turn() === 'w' ? 'black' : 'white';
    }

    // If we have game result info for win, use that
    if (gameResultInfo) {
      if (gameResultInfo.result === '1-0') {
        winner = 'white';
      } else if (gameResultInfo.result === '0-1') {
        winner = 'black';
      }
    }

    setGameState({ isCheckmate, isCheck, isDraw, winner });

    // Update king positions
    const positions = findKingPositions(chess.fen());
    setKingPositions(positions);
  };

  // Function to make a chess move from SAN notation (for Stockfish integration)
  const makeChessMove = (sanMove) => {
    try {
      if (!game || !sanMove) return false;

      // Get the current position based on our current move
      const currentFen = getCurrentBoardPosition(game, currentMove);
      const chess = new Chess(currentFen);

      // Try to make the move using SAN notation
      const move = chess.move(sanMove);

      // If move is null, it's an invalid chess move
      if (move === null) {
        console.error(`Board.jsx: Move ${sanMove} is not valid for current position`);
        return false;
      }

      // Now we need to update our game state appropriately using the existing move handling
      const gameCopy = game;
      const { prefix, currMoveNum } = parseMove(currentMove);
      let nextMoveIdx = `${prefix}${currMoveNum + 1}`;

      let updatedPostMoves = JSON.parse(JSON.stringify(moves));
      const nextMove = getMove(updatedPostMoves, nextMoveIdx);

      if (nextMove) {
        // There's already a next move in our tree
        if (nextMove.notation === move.san) {
          // If the next move is the one we're trying to make, just navigate to it
          setCurrentMove(nextMoveIdx);

          // Set last moved square for highlighting
          setLastMovedSquare(move.to);

          // Update game state (check, checkmate, etc.)
          updateGameState(chess);

          return true;
        }

        // The move is different, so we need to create a variation
        const currentMoveRef = getMove(updatedPostMoves, currentMove);
        if (!currentMoveRef.variations) {
          currentMoveRef.variations = [];
        }

        currentMoveRef.variations.push([createMove(move.san, isCommenting && MOVE_TYPES.COMMENT)]);

        const numVariations = currentMoveRef.variations.length;
        nextMoveIdx = `${prefix}${currMoveNum}|${numVariations - 1}:0`;

        // Always set the last moved square, even for variations
        setLastMovedSquare(move.to);
      } else {
        // This is a new move to add to our tree
        const move = gameCopy.move(sanMove);

        if (move === null) {
          console.error(`Board.jsx: Game.move(${sanMove}) failed unexpectedly`);
          return false;
        }

        const lines = getLines(currentMove);
        let currentMoves = updatedPostMoves;

        if (!currentMoves) {
          return false;
        }

        let nextMove;
        for (let line of lines) {
          const [variationNum, movesInThisLine] = destructureLine(line);

          if (variationNum) {
            currentMoves = nextMove.variations[variationNum];
          }

          nextMove = currentMoves[movesInThisLine];
        }

        currentMoves.push(createMove(move.san, isCommenting && MOVE_TYPES.COMMENT));

        // Update PGN if needed
        setPgn && setPgn(gameCopy.pgn());
      }

      // Update moves and current move state
      setMoves(updatedPostMoves);
      setCurrentMove(nextMoveIdx);

      // Highlight source and target squares
      const sourceSquare = move.from;
      const targetSquare = move.to;

      // Set the last moved square for highlighting
      setLastMovedSquare(targetSquare);

      // Create highlight styles for the move
      const highlightSquares = {
        [sourceSquare]: currentTheme.moveFrom || DEFAULT_MOVE_HIGHLIGHT.moveFrom,
        [targetSquare]: currentTheme.moveTo || DEFAULT_MOVE_HIGHLIGHT.moveTo
      };
      setMoveSquares(highlightSquares);

      // Update game state (check, checkmate, etc.)
      updateGameState(chess);

      return true;
    } catch (error) {
      return false;
    }
  };

  // Function to make multiple sequential chess moves
  const makeSequentialMoves = (moveArray) => {
    if (!moveArray || !Array.isArray(moveArray) || moveArray.length === 0) {
      console.error("Board.jsx: Invalid moves array provided");
      return false;
    }


    try {
      // Apply each move in sequence
      for (let i = 0; i < moveArray.length; i++) {
        const result = makeChessMove(moveArray[i]);
        if (!result) {
          console.error(`Board.jsx: Failed to make move ${moveArray[i]} (move ${i + 1} in sequence)`);
          return false;
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  // Function to set the board position directly to a specific FEN
  const setPositionDirectly = (fen) => {
    try {
      if (!fen || typeof fen !== 'string') {
        console.error("Board.jsx: Invalid FEN provided");
        return false;
      }


      // Create a new Chess instance with the provided FEN
      const newGame = new Chess(fen);

      // TODO: Implement logic to properly update the game state when setting position directly
      // This is a placeholder for future implementation

      return true;
    } catch (error) {
      console.error("Board.jsx: Error setting position directly: ", error);
      return false;
    }
  };

  // Function to reset the chess board to the starting position
  const resetChessBoard = () => {
    try {

      // TODO: Implement logic to reset the board to starting position
      // This is a placeholder for future implementation

      return true;
    } catch (error) {
      return false;
    }
  };

  // Function to check if a move is legal
  const isLegalMove = (sanMove) => {
    try {
      if (!game || !sanMove) return false;

      // Get the current position based on our current move
      const currentFen = getCurrentBoardPosition(game, currentMove);
      const chess = new Chess(currentFen);

      // Try to make the move using SAN notation to check legality
      const move = chess.move(sanMove);

      // If move is successful, undo it and return true
      if (move !== null) {
        chess.undo();
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  // Expose functions to window for Stockfish.js to use
  useEffect(() => {
    // Make chess move function available globally
    window.makeChessMove = makeChessMove;

    // Make legality check function available globally
    window.isLegalMove = isLegalMove;

    // Make current board position function available globally
    window.getCurrentBoardPosition = () => getCurrentBoardPosition(game, currentMove);

    // Make current move index available globally
    window.getCurrentMoveIndex = () => currentMove;

    // Add new functions for sequential moves and position manipulation
    window.makeSequentialMoves = makeSequentialMoves;
    window.setPositionDirectly = setPositionDirectly;
    window.resetChessBoard = resetChessBoard;

    // Add getMove function to access move data
    window.getMove = getMove;

    // Add reference to moves array for Stockfish.js
    window.moves = moves;
    
    // Add reference to resetPositionCache
    if (typeof resetPositionCache === 'function') {
      window.resetChessPositionCache = resetPositionCache;
    }

    // Cleanup when component unmounts
    return () => {
      window.makeChessMove = undefined;
      window.isLegalMove = undefined;
      window.getCurrentBoardPosition = undefined;
      window.getCurrentMoveIndex = undefined;
      window.makeSequentialMoves = undefined;
      window.setPositionDirectly = undefined;
      window.resetChessBoard = undefined;
      window.getMove = undefined;
      window.moves = undefined;
      window.resetChessPositionCache = undefined;
    };
  }, [game, currentMove, moves, makeChessMove, isLegalMove, makeSequentialMoves, setPositionDirectly, resetChessBoard]);

  // Add a ref to track arrows created in the current session
  const userCreatedArrows = useRef(new Set());

  const onPostBoardDrop = (sourceSquare, targetSquare) => {
    try {
      const gameCopy = game;

      const { prefix, currMoveNum } = parseMove(currentMove);
      let nextMoveIdx = `${prefix}${currMoveNum + 1}`;

      let updatedPostMoves = JSON.parse(JSON.stringify(moves));
      const nextMove = getMove(updatedPostMoves, nextMoveIdx);
      if (nextMove) {
        const currentFen = targetPosition; // Use memoized position instead of recalculating
        const chess = new Chess(currentFen);
        const move = chess.move({ from: sourceSquare, to: targetSquare, promotion: '' });

        // If move is null, it's an invalid chess move
        if (move === null) return false;

        // Check if we need to validate the move (for puzzles)
        if (onValidateMove) {
          // If onValidateMove returns false, don't record this move
          if (!onValidateMove(move)) {
            return false;
          }
        }

        if (nextMove.notation === move.san) {
          // Prevent duplicate processing by updating the lastProcessedMoveRef
          lastProcessedMoveRef.current = nextMoveIdx;
          setCurrentMove(nextMoveIdx);

          // Set last moved square even when following pre-existing moves
          setLastMovedSquare(targetSquare);

          return false;
        }

        // Only create variations if allowed (not a puzzle or explicitly permitted)
        if (!shouldAllowVariation(move)) {
          return false;
        }

        const currentMoveRef = getMove(updatedPostMoves, currentMove);
        if (!currentMoveRef.variations) {
          currentMoveRef.variations = [];
        }

        currentMoveRef.variations.push([createMove(move.san, isCommenting && MOVE_TYPES.COMMENT)]);

        const numVariations = currentMoveRef.variations.length;

        // Reset position cache when creating a new variation to ensure fresh position calculations
        resetPositionCache();
        
        nextMoveIdx = `${prefix}${currMoveNum}|${numVariations - 1}:0`;

        // IMPORTANT: Always set the last moved square for all types of moves
        setLastMovedSquare(targetSquare);
      } else {
        const move = gameCopy.move({ from: sourceSquare, to: targetSquare, promotion: 'q' });

        if (move === null) {
          return false;
        }

        // Check if we need to validate the move (for puzzles)
        if (onValidateMove) {
          // If onValidateMove returns false, don't record this move
          if (!onValidateMove(move)) {
            return false;
          }
        }

        const lines = getLines(currentMove);
        let moves = updatedPostMoves;

        if (!moves) {
          return;
        }

        let nextMove;
        for (let line of lines) {
          const [variationNum, movesInThisLine] = destructureLine(line);

          if (variationNum) {
            moves = nextMove.variations[variationNum];
          }

          nextMove = moves[movesInThisLine];
        }

        moves.push(createMove(move.san, isCommenting && MOVE_TYPES.COMMENT));

        setPgn && setPgn(gameCopy.pgn());
      }

      setMoves(updatedPostMoves);
      setCurrentMove(nextMoveIdx);

      // Set the last moved square for emoji display
      setLastMovedSquare(targetSquare);

      // Highlight the start and end squares of the move
      const highlightSquares = {
        [sourceSquare]: currentTheme.moveFrom || DEFAULT_MOVE_HIGHLIGHT.moveFrom,
        [targetSquare]: currentTheme.moveTo || DEFAULT_MOVE_HIGHLIGHT.moveTo
      };
      setMoveSquares(highlightSquares);

      // After making a move, update the game state
      if (gameCopy) {
        updateGameState(gameCopy);
      }

      // Prevent duplicate processing by updating the lastProcessedMoveRef
      lastProcessedMoveRef.current = nextMoveIdx;
      setCurrentMove(nextMoveIdx);

      return true;
    } catch (error) {
      console.error("An error occurred while making a move on post board: ", error);
      return false;
    }
  };

  // Modify the arrow handling to track user-created arrows
  const onArrowsChange = (rawArrows) => {
    if (rawArrows.length === 0) {
      return;
    }
    const arrows = rawArrows.map(arrow => ([arrow[0], arrow[1], activeColor]));
    const newPostMoves = JSON.parse(JSON.stringify(moves));
    if (newPostMoves[currentMove]) {
      // Track this arrow as user-created
      const arrowKey = `${arrows[0][0]}-${arrows[0][1]}`;
      userCreatedArrows.current.add(arrowKey);

      newPostMoves[currentMove].arrows = [...(newPostMoves[currentMove].arrows || []), arrows[0]];
      setMoves([...newPostMoves]);
      // Force update to ensure arrows are visible immediately
      setTimeout(() => {
        setActiveColor(prev => prev); // This triggers a re-render
      }, 0);
      return;
    }

    const lines = getLines(currentMove);
    let updatedPostMoves = JSON.parse(JSON.stringify(moves));
    let newMoves = updatedPostMoves;

    if (!newMoves) {
      return;
    }

    let nextMove;
    for (let line of lines) {
      const [variationNum, movesInThisLine] = destructureLine(line);

      if (variationNum) {
        newMoves = nextMove.variations[variationNum];
      }

      nextMove = newMoves[movesInThisLine];
    }

    if (nextMove) {
      nextMove.arrows = [...(nextMove.arrows || []), ...arrows];
    }

    setMoves(updatedPostMoves);
    // Force update to ensure arrows are visible immediately
    setTimeout(() => {
      setActiveColor(prev => prev); // This triggers a re-render
    }, 0);
  }

  // Combine Stockfish arrows (from context) and user-drawn arrows (from move data)
  const getCombinedArrows = () => {
    const userDrawnArrows = getMove(moves, currentMove)?.arrows || [];
    // stockfishArrows already respects the 'NONE', 'BEST', 'ALL' setting via Stockfish.js


    const combined = [...(stockfishArrows || []), ...userDrawnArrows];



    return combined;
  };

  const handleSquareClick = (square) => {
    const newMoves = JSON.parse(JSON.stringify(moves));
    const move = getMove(newMoves, currentMove);
    const arrows = move?.arrows;
    const filteredArrows = arrows?.filter(([from, to]) => from !== square && to !== square);
    if (filteredArrows) {
      move.arrows = JSON.parse(JSON.stringify(filteredArrows));
      setMoves(newMoves);
    }
  };

  const handleSquareRightClick = (square) => {
    const newMoves = JSON.parse(JSON.stringify(moves));
    const move = getMove(newMoves, currentMove);
    if (!move) {
      return;
    }
    let highlightedSquares = move?.highlightedSquares;

    if (!highlightedSquares) {
      highlightedSquares = {};
    }

    if (highlightedSquares[square]) {
      delete highlightedSquares[square];
    } else {
      highlightedSquares[square] = { backgroundColor: activeColor };
    }
    move.highlightedSquares = JSON.parse(JSON.stringify(highlightedSquares));

    setMoves(newMoves);
  };

  useEffect(() => {
    const handleResize = () => {
      setActiveColor(prev => prev);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add a more direct way to force the draw state immediately when a result is detected
  const forceGameStateFromResult = (result) => {
    if (!result) return;


    // Make sure we have the latest king positions
    const currentFen = getCurrentBoardPosition(game, currentMove);
    if (currentFen) {
      const positions = findKingPositions(currentFen);
      setKingPositions(positions);
    }

    // Check if this is the last move (only apply state changes for last move)
    const moveArray = Array.isArray(moves) ? moves : [];
    const isLastMove = moveArray.length > 0 && (
      currentMove === (moveArray.length - 1).toString() ||
      currentMove === (moveArray.length).toString()
    );

    // Only update game state if we're on the last move
    if (isLastMove) {
      if (result === '1/2-1/2' || (result && result.toLowerCase && result.toLowerCase() === 'draw')) {
        setGameState(prevState => ({
          ...prevState,
          isDraw: true,
          isCheck: false,
          isCheckmate: false,
          winner: null
        }));
      } else if (result === '1-0') {
        setGameState(prevState => ({
          ...prevState,
          winner: 'white',
          isDraw: false
        }));
      } else if (result === '0-1') {
        setGameState(prevState => ({
          ...prevState,
          winner: 'black',
          isDraw: false
        }));
      }
    }
  };

  // Modify the effect that handles the last move to use our new function
  useEffect(() => {
    // Check if this is the last move in the sequence
    if (!moves || !currentMove) return;

    // Make sure we always have updated king positions
    const currentFen = getCurrentBoardPosition(game, currentMove);
    if (currentFen) {
      const positions = findKingPositions(currentFen);
      setKingPositions(positions);
    }

    const moveArray = Array.isArray(moves) ? moves : [];
    let isLastMove = false;

    // Test if we're at the last main line move
    if (moveArray.length > 0 && (
      currentMove === (moveArray.length - 1).toString() ||
      currentMove === (moveArray.length).toString()
    )) {
      isLastMove = true;
    }

    // If this is the last move and we have a game result, apply it
    if (isLastMove && gameResultInfo) {
      forceGameStateFromResult(gameResultInfo.result);
    }
  }, [currentMove, gameResultInfo, moves, game]);

  // Make another direct effect for explicit handling of gameResultInfo changes
  useEffect(() => {
    if (gameResultInfo && gameResultInfo.result) {
      forceGameStateFromResult(gameResultInfo.result);
    }
  }, [gameResultInfo]);

  // Extra check - inspect the raw game data from chess.js for results with higher priority
  useEffect(() => {
    if (game) {
      // Try to extract the game result from the raw PGN
      const pgn = game.pgn();
      if (pgn) {
        if (pgn.includes('1/2-1/2')) {
          setGameState(prevState => ({
            ...prevState,
            isDraw: true,
            isCheck: false,
            isCheckmate: false,
            winner: null
          }));

          // Make sure king positions are up to date
          const currentFen = getCurrentBoardPosition(game, currentMove);
          if (currentFen) {
            const positions = findKingPositions(currentFen);
            setKingPositions(positions);
          }
        }
      }
    }
  }, [game, currentMove]);

  // Add initialization effect to ensure king positions are set correctly at startup
  useEffect(() => {
    // Get the current position when component mounts
    const currentFen = getCurrentBoardPosition(game, currentMove);
    if (currentFen) {
      const positions = findKingPositions(currentFen);
      setKingPositions(positions);

      // Check for draw result on initial render
      if (gameResultInfo && gameResultInfo.result === '1/2-1/2') {
        setGameState(prevState => ({
          ...prevState,
          isDraw: true,
          isCheck: false,
          isCheckmate: false,
          winner: null
        }));
      }
    }
  }, []);

  // Add function to manually set draw state for debugging
  const forceDrawState = () => {
    const currentFen = getCurrentBoardPosition(game, currentMove);
    if (currentFen) {
      const positions = findKingPositions(currentFen);
      setKingPositions(positions);

      // Check if this is the last move
      const moveArray = Array.isArray(moves) ? moves : [];
      const isLastMove = moveArray.length > 0 && (
        currentMove === (moveArray.length - 1).toString() ||
        currentMove === (moveArray.length).toString()
      );

      if (isLastMove) {
        setGameState({
          isDraw: true,
          isCheck: false,
          isCheckmate: false,
          winner: null
        });
      } else {
      }
    }
  };

  // Call forceDrawState when gameResultInfo shows a draw
  useEffect(() => {
    if (gameResultInfo && gameResultInfo.result === '1/2-1/2') {
      forceDrawState();
    }
  }, [gameResultInfo]);

  // Add extra effect to log king positions when they change
  useEffect(() => {
    if (gameState.isDraw) {
    }
  }, [kingPositions, gameState.isDraw]);

  // Modify buildCustomStyles to prioritize user-defined highlights over automatic ones
  const buildCustomStyles = () => {
    // First get the user-defined highlighted squares from the current move
    const userHighlightedSquares = getMove(moves, currentMove)?.highlightedSquares || {};

    // Start with move squares (the automatic yellow highlights)
    let styles = { ...moveSquares };

    // Then override with user-defined highlights (giving them priority)
    // This is the key change - user highlighted squares take precedence
    Object.entries(userHighlightedSquares).forEach(([square, style]) => {
      styles[square] = { ...style }; // Replace completely, don't merge
    });



    // Add color highlighting for kings based on game state
    if (kingPositions.white && kingPositions.black) {
      // Check if this is the last move
      const moveArray = Array.isArray(moves) ? moves : [];
      const isLastMove = moveArray.length > 0 && (
        currentMove === (moveArray.length - 1).toString() ||
        currentMove === (moveArray.length).toString()
      );

      // First check for explicit draw state from multiple sources
      const isDraw = gameState.isDraw ||
        (gameResultInfo &&
          gameResultInfo.result === '1/2-1/2' ||
          (gameResultInfo &&
            gameResultInfo.result &&
            gameResultInfo.result.toLowerCase() === 'draw'));

      if (isDraw && isLastMove) {
        // Only apply if user hasn't manually highlighted these squares
        if (!userHighlightedSquares[kingPositions.white]) {
          styles[kingPositions.white] = {
            ...styles[kingPositions.white],
            backgroundColor: 'tan' // Tan with transparency
          };
        }

        if (!userHighlightedSquares[kingPositions.black]) {
          styles[kingPositions.black] = {
            ...styles[kingPositions.black],
            backgroundColor: 'tan' // Tan with transparency
          };
        }
      }
      // Checkmate scenario - green for winning king, red for losing king
      else if (gameState.isCheckmate && gameState.winner) {
        const winningKing = kingPositions[gameState.winner];
        const losingKing = kingPositions[gameState.winner === 'white' ? 'black' : 'white'];

        // Only apply if user hasn't manually highlighted these squares
        if (!userHighlightedSquares[winningKing]) {
          styles[winningKing] = {
            ...styles[winningKing],
            backgroundColor: 'rgba(0, 128, 0, 0.5)' // Green with transparency
          };
        }

        if (!userHighlightedSquares[losingKing]) {
          styles[losingKing] = {
            ...styles[losingKing],
            backgroundColor: 'rgba(220, 0, 0, 0.5)' // Red with transparency
          };
        }
      }
    }



    return styles;
  };

  // Helper function to convert chess notation to coordinates
  const squareToCoordinates = (square) => {
    const file = square.charCodeAt(0) - 'a'.charCodeAt(0);
    const rank = 8 - parseInt(square[1]);
    return { file, rank };
  };

  // Modify renderIconsOverlay to only show draw icons on last move
  const renderIconsOverlay = () => {
    const overlayElements = [];

    // Add emoji icons if enabled
    if (showEmoji && Object.keys(emojiSquares).length > 0) {
      Object.entries(emojiSquares).forEach(([square, style]) => {
        const { file, rank } = squareToCoordinates(square);
        // Use raw SVG content or URL based on what's stored in emojiIcons
        const emojiSrc = emojiIcons[style];

        overlayElements.push(
          <div
            key={`emoji-${square}`}
            style={{
              position: 'absolute',
              top: `${rank * squareSize + -4}px`,
              left: `${file * squareSize + -5}px`,
              width: '36px',
              height: '36px',
              backgroundImage: emojiSrc,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              pointerEvents: 'none',
              zIndex: 10
            }}
          />
        );
      });
    }

    // Add stockfish icons if enabled
    if (showStockfishIcons && Object.keys(stockfishSquares).length > 0) {
      Object.entries(stockfishSquares).forEach(([square, style]) => {
        const { file, rank } = squareToCoordinates(square);
        // Get the actual icon URL
        const iconSrc = stockfishIcons[style];

        overlayElements.push(
          <img
            key={`stockfish-${square}`}
            src={iconSrc}
            alt={`${style} move`}
            style={{
              position: 'absolute',
              top: `${rank * squareSize + -6}px`,
              left: `${file * squareSize + (squareSize - 15)}px`, // Position on right side
              width: '24px',
              height: '24px',
              pointerEvents: 'none',
              zIndex: 10000
            }}
          />
        );
      });
    }

    // Check if this is the last move
    const moveArray = Array.isArray(moves) ? moves : [];
    const isLastMove = moveArray.length > 0 && (
      currentMove === (moveArray.length - 1).toString() ||
      currentMove === (moveArray.length).toString()
    );

    // Add game state icons - ALWAYS VISIBLE but only on appropriate moves
    if (kingPositions.white && kingPositions.black) {
      // First check for explicit draw state from multiple sources
      const isDraw = gameState.isDraw ||
        (gameResultInfo &&
          gameResultInfo.result === '1/2-1/2' ||
          (gameResultInfo &&
            gameResultInfo.result &&
            gameResultInfo.result.toLowerCase() === 'draw'));

      if (isDraw && isLastMove) {
        // Add draw icon to both kings
        [kingPositions.white, kingPositions.black].forEach((kingSquare, index) => {
          if (kingSquare) {
            const { file, rank } = squareToCoordinates(kingSquare);
            overlayElements.push(
              <img
                key={`draw-king-${index}`}
                src={drawIcon}
                alt="Draw"
                style={{
                  position: 'absolute',
                  top: `${rank * squareSize + -6}px`,
                  left: `${file * squareSize + (squareSize - 15)}px`,
                  width: '24px',
                  height: '24px',
                  pointerEvents: 'none',
                  zIndex: 11
                }}
              />
            );
          }
        });
      }
      // Handle checkmate scenario - always visible
      else if (gameState.isCheckmate && gameState.winner) {
        // Find winning king position
        const winningKingSquare = gameState.winner === 'white' ? kingPositions.white : kingPositions.black;
        const losingKingSquare = gameState.winner === 'white' ? kingPositions.black : kingPositions.white;

        if (winningKingSquare) {
          const { file: winFile, rank: winRank } = squareToCoordinates(winningKingSquare);
          overlayElements.push(
            <img
              key={`winner-king`}
              src={winnerIcon}
              alt="Winning king"
              style={{
                position: 'absolute',
                top: `${winRank * squareSize + -6}px`,
                left: `${winFile * squareSize + (squareSize - 15)}px`,
                width: '24px',
                height: '24px',
                pointerEvents: 'none',
                zIndex: 11 // Higher than normal stockfish icons
              }}
            />
          );
        }

        if (losingKingSquare) {
          const { file: loseFile, rank: loseRank } = squareToCoordinates(losingKingSquare);
          overlayElements.push(
            <img
              key={`loser-king`}
              src={loserIcon}
              alt="Losing king"
              style={{
                position: 'absolute',
                top: `${loseRank * squareSize + -6}px`,
                left: `${loseFile * squareSize + (squareSize - 15)}px`,
                width: '24px',
                height: '24px',
                pointerEvents: 'none',
                zIndex: 11
              }}
            />
          );
        }
      }
      // Handle check scenario (but not checkmate) - always visible
      else if (gameState.isCheck) {
        // The king in check is the one whose turn it is in the current position
        const currentFen = getCurrentBoardPosition(game, currentMove);
        const tempChess = new Chess(currentFen);
        const kingInCheckColor = tempChess.turn() === 'w' ? 'white' : 'black';
        const kingInCheckSquare = kingPositions[kingInCheckColor];

        if (kingInCheckSquare) {
          const { file, rank } = squareToCoordinates(kingInCheckSquare);
          overlayElements.push(
            <img
              key={`check-king`}
              src={checkIcon}
              alt="King in check"
              style={{
                position: 'absolute',
                top: `${rank * squareSize + -6}px`,
                left: `${file * squareSize + (squareSize - 15)}px`,
                width: '24px',
                height: '24px',
                pointerEvents: 'none',
                zIndex: 11
              }}
            />
          );
        }
      }
    }

    return overlayElements;
  };

  

  // Prevent unnecessary rerenders by comparing with the last rendered position
  useEffect(() => {
    console.log('[BOARD-FEN] Position useEffect:', {
      targetPosition,
      lastRendered: lastRenderedPositionRef.current,
      isDifferent: targetPosition !== lastRenderedPositionRef.current
    });
    
    if (targetPosition && targetPosition !== lastRenderedPositionRef.current) {
      
      lastRenderedPositionRef.current = targetPosition;
      
      // Update king positions and game state only when position actually changes
      if (targetPosition) {
        const tempChess = new Chess(targetPosition);
        updateGameState(tempChess);
      }
      
      // Notify parent about FEN change only when position actually changes
      if (onFenChange) {
        onFenChange(targetPosition);
      }
    }
  }, [targetPosition, onFenChange]);

  // Add a useEffect to preemptively calculate variation positions when the user is navigating
  useEffect(() => {
    // Don't preload if we're already in a variation to avoid recursion
    if (!currentMove.includes('|')) {
      // Check if current move has variations
      const currentMoveObj = getMove(moves, currentMove);
      if (currentMoveObj && currentMoveObj.variations && currentMoveObj.variations.length > 0) {
        // Preload the first move of the first variation
        const variationNotation = `${currentMove}|0:0`;
        
        
        // Calculate this position and store in cache without rendering
        setTimeout(() => {
          const preloadPosition = getCurrentBoardPosition(game, variationNotation);
          
        }, 100);
      }
    }
  }, [currentMove, moves, game]);
  
  // Update the keyboard event handling in Controls component by preloadingVariation
  useEffect(() => {
    // Expose a method to preload variations when hovering arrow keys
    window.preloadVariationPosition = (variationNotation) => {
      if (variationNotation && variationNotation.includes('|')) {
        
        const preloadPosition = getCurrentBoardPosition(game, variationNotation);
        return preloadPosition;
      }
      return null;
    };
    
    return () => {
      window.preloadVariationPosition = undefined;
    };
  }, [game]);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxSizing: 'border-box',
      position: 'relative' // Add position relative for absolute positioning of children
    }}>
      {console.log('[BOARD-FEN] Rendering chessboard with position:', targetPosition)}
      
      <Chessboard
        id="PostBoard"
        boardWidth={boardSize}
        boardOrientation={orientation}
        // position={getCurrentBoardPosition(game, currentMove)}
        position={targetPosition} // Use memoized position instead of direct calculation
        onPieceDrop={isDraggingDisabled ? null : onPostBoardDrop}
        customDarkSquareStyle={boardTheme === 'custom' && customColors
          ? { background: customColors.dark }
          : currentTheme.dark}
        customLightSquareStyle={boardTheme === 'custom' && customColors
          ? { background: customColors.light }
          : currentTheme.light}
        showBoardNotation={shouldShowCoordinates}
        customSquareStyles={buildCustomStyles()}
        customArrows={getCombinedArrows()}
        onArrowsChange={isDraggingDisabled ? null : onArrowsChange}
        onSquareClick={handleSquareClick}
        onSquareRightClick={isDraggingDisabled ? null : handleSquareRightClick}
        arePiecesDraggable={!isDraggingDisabled}
        promotionDialogVariant={'vertical'}
      />
      {/* Render the icons overlay */}
      {renderIconsOverlay()}
    </div>
  )
};

export default Board;
